import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../api';
import SalesIntelSection, { PERIODS } from './SalesIntelSection';
import SiBarChart from './SiChart';
import SalesIntelMaths from './SalesIntelMaths';

const fmtMoney = (n, cur) => (cur === 'USD' ? 'US$' : 'A$') + Math.round(n || 0).toLocaleString();

// Tabs keep one long page readable. Every tab reads the same filters above it.
const TAB_DEFS = [
  { id: 'overview', label: 'Overview', blurb: 'Headline numbers and daily activity', show: p => p.overall || p.stale },
  { id: 'people', label: 'Team & Clients', blurb: 'Who is quoting, who is buying', show: p => p.overall },
  { id: 'items', label: 'Items & Stock', blurb: 'Demand against stock on hand, plus item-level forecasts with order-history drilldowns', show: p => p.items },
  { id: 'forecast', label: 'Sales/Quote Forecasts', blurb: 'Weighted quote and sales-order projections over 30 days to 12 months', show: p => p.forecast && p.overall },
  { id: 'finance', label: 'Finance', blurb: 'Booked revenue, month by month', show: p => p.deep },
  { id: 'probability', label: 'Win Probability', blurb: 'Scored open opportunities and alerts', show: p => p.deep },
  { id: 'insights', label: 'Deep Insights', blurb: 'Pipeline health and team leaderboard', show: p => p.deep },
  { id: 'maths', label: 'Maths', blurb: 'Every formula on this page, explained', show: () => true },
];
const SECTION_TABS = ['overview', 'people', 'items', 'forecast'];

function Chips({ label, hint, items, value, onChange, accent }) {
  return (
    <div className="si-fg" data-testid={'si-filter-' + label.toLowerCase()}>
      <span className="si-fg-lbl" title={hint || undefined}>{label}</span>
      <div className={'si-fg-chips' + (accent ? ' accent' : '')} role="tablist">
        {items.map(([v, l, testid]) => (
          <button key={testid} role="tab" aria-selected={value === v} className={'si-chip' + (value === v ? ' on' : '')}
            data-testid={testid} onClick={() => onChange(v)}>{l}</button>
        ))}
      </div>
    </div>
  );
}

export default function SalesIntelPage({ toast, perms = {} }) {
  const [days, setDays] = useState(30);
  const [segment, setSegment] = useState('');
  const [subsidiary, setSubsidiary] = useState('');
  const [subs, setSubs] = useState([]);
  const [focus, setFocus] = useState('combined');
  useEffect(() => { api.salesIntelSubsidiaries().then(d => setSubs(d.items || [])).catch(() => {}); }, []);

  const tabs = useMemo(() => TAB_DEFS.filter(t => t.show(perms)), [perms]);
  const [tab, setTab] = useState(null);
  const active = tab && tabs.some(t => t.id === tab) ? tab : (tabs[0]?.id || 'overview');
  // Panels stay mounted once opened so switching tabs never re-fetches.
  const [opened, setOpened] = useState(() => ({}));
  useEffect(() => { setOpened(o => (o[active] ? o : { ...o, [active]: true })); }, [active]);
  const isOpen = id => !!opened[id] || id === active;

  const sections = {
    kpis: active === 'overview' && !!perms.overall,
    tables: active === 'people' && !!perms.overall,
    items: active === 'items' && !!perms.items,
    itemForecast: active === 'items' && !!perms.items && !!perms.forecast,
    forecast: active === 'forecast' && !!perms.forecast && !!perms.overall,
    stale: active === 'overview' && !!perms.stale,
  };
  const activeTab = tabs.find(t => t.id === active);

  return (
    <div className="si-page" data-testid="sales-intel-page">
      <header className="si-topbar">
        <div className="si-topbar-row">
          <div>
            <h2 className="si-title">Sales Intelligence</h2>
            <p className="si-title-sub">Quotes, orders, pipeline and forecasting — read straight from NetSuite</p>
          </div>
          <div className="si-filters" style={active === 'maths' ? { display: 'none' } : undefined}>
            <Chips label="Period" items={PERIODS.map(([d, l]) => [d, l, 'si-period-' + (l === 'Year to date' ? 'ytd' : d)])}
              value={days} onChange={setDays} hint="Window used by every table and chart on this page" />
            <Chips label="Segment" items={[['', 'All', 'si-seg-all'], ['Retail', 'Retail', 'si-seg-retail'], ['Commercial', 'Commercial', 'si-seg-commercial']]}
              value={segment} onChange={setSegment} hint="From the linked Opportunity's type in NetSuite (falls back to the quote/order's own class)" />
            {subs.length > 0 && (
              <Chips label="Subsidiary" value={subsidiary} onChange={setSubsidiary}
                hint="Switches every view, including finance and win probability"
                items={[['', 'All', 'si-sub-all'], ...subs.map(s => [s, s, 'si-sub-' + s.replace(/\s+/g, '-').toLowerCase()])]} />
            )}
            <Chips label="Showing" accent value={focus} onChange={setFocus}
              hint="Quotes only, sales orders only, or both side by side"
              items={[['quotes', 'Quotes', 'si-focus-quotes'], ['orders', 'Sales Orders', 'si-focus-orders'], ['combined', 'Combined', 'si-focus-combined']]} />
          </div>
        </div>
        <nav className="si-tabs" role="tablist" data-testid="si-tabs">
          {tabs.map(t => (
            <button key={t.id} role="tab" aria-selected={active === t.id} title={t.blurb}
              className={'si-tab' + (active === t.id ? ' on' : '')} data-testid={'si-tab-' + t.id}
              onClick={() => setTab(t.id)}>{t.label}</button>
          ))}
        </nav>
        {activeTab && <p className="si-tab-blurb" data-testid="si-tab-blurb">{activeTab.blurb}</p>}
      </header>

      <div className="si-tab-panel" data-testid={'si-panel-' + active}>
        <div style={SECTION_TABS.includes(active) ? undefined : { display: 'none' }}>
          <SalesIntelSection bare canSync={!!perms.overall} toast={toast} days={days} onDaysChange={setDays}
            segment={segment} subsidiary={subsidiary} focus={focus} sections={sections} />
        </div>
        {perms.deep && isOpen('finance') && <div style={active === 'finance' ? undefined : { display: 'none' }}><OrdersFinance segment={segment} subsidiary={subsidiary} toast={toast} /></div>}
        {perms.deep && isOpen('probability') && <div style={active === 'probability' ? undefined : { display: 'none' }}><ProbabilityAlerts subsidiary={subsidiary} /><WinProbability subsidiary={subsidiary} /></div>}
        {perms.deep && isOpen('insights') && <div style={active === 'insights' ? undefined : { display: 'none' }}><DeepInsights days={days} segment={segment} subsidiary={subsidiary} /></div>}
        {active === 'maths' && <SalesIntelMaths />}
      </div>
    </div>
  );
}

function DeepInsights({ days, segment, subsidiary }) {
  const [d, setD] = useState(null);
  const [exporting, setExporting] = useState('');
  const load = useCallback(() => { api.salesIntelInsights(days, segment, subsidiary).then(setD).catch(() => {}); }, [days, segment, subsidiary]);
  useEffect(() => { load(); }, [load]);
  async function exportClient(name) {
    setExporting(name);
    try {
      const blob = await api.salesIntelDrillExport('client', name, 'creator', days, segment, subsidiary);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'sales-report-' + name.replace(/\s+/g, '-').toLowerCase() + '-' + (days === 365 ? '12mo' : days + 'd') + '.xlsx';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { /* silent */ }
    setExporting('');
  }
  if (!d) return <section className="dash-card si-card"><div className="empty-note">Loading deep insights…</div></section>;
  const cur = d.currency;
  const p = d.pipeline || {};
  const agingLabels = ['0-30', '31-60', '61-90', '90+'];
  const agingMax = Math.max(...agingLabels.map(k => (p.aging?.[k]?.count) || 0), 1);
  const funnelOrder = ['Open', 'Processed', 'Closed', 'Expired'];
  const funnelMax = Math.max(...funnelOrder.map(k => d.funnel?.[k]?.count || 0), 1);

  return (
    <>
      <section className="dash-card si-card" data-testid="si-insights">
        <div className="dash-card-head si-head"><h3>Deep Insights <span className="si-forecast-basis">window: {days === 365 ? '12 months' : 'last ' + days + ' days'}</span></h3></div>

        <div className="si-kpis" data-testid="si-pipeline-kpis">
          <div className="si-kpi"><span className="si-kpi-num">{(p.open_count || 0).toLocaleString()}</span><span className="si-kpi-lbl">Open quotes (live pipeline)</span></div>
          <div className="si-kpi"><span className="si-kpi-num">{fmtMoney(p.open_amount, cur)}</span><span className="si-kpi-lbl">Pipeline value</span></div>
          <div className="si-kpi"><span className="si-kpi-num">{d.averages?.avg_quote_value != null ? fmtMoney(d.averages.avg_quote_value, cur) : '—'}</span><span className="si-kpi-lbl">Avg quote value</span></div>
          <div className="si-kpi"><span className="si-kpi-num">{d.averages?.avg_order_value != null ? fmtMoney(d.averages.avg_order_value, cur) : '—'}</span><span className="si-kpi-lbl">Avg order value</span></div>
          <div className="si-kpi"><span className="si-kpi-num">{(d.clients?.new || 0).toLocaleString()}</span><span className="si-kpi-lbl">New clients · {(d.clients?.returning || 0).toLocaleString()} returning</span></div>
        </div>

        <div className="si-grid">
          <div className="si-panel" data-testid="si-aging">
            <h4>Pipeline aging (days since activity)</h4>
            {agingLabels.map(k => {
              const b = p.aging?.[k] || { count: 0, amount: 0 };
              return (
                <div className="si-hbar-row" key={k}>
                  <span className="si-hbar-lbl">{k}d</span>
                  <div className="si-hbar-track"><div className={'si-hbar' + (k === '90+' ? ' bad' : k === '61-90' ? ' warn' : '')} style={{ width: Math.max(3, b.count / agingMax * 100) + '%' }} /></div>
                  <span className="si-hbar-val">{b.count} · {fmtMoney(b.amount, cur)}</span>
                </div>
              );
            })}
          </div>
          <div className="si-panel" data-testid="si-funnel">
            <h4>Quote funnel</h4>
            {funnelOrder.filter(k => d.funnel?.[k]).map(k => {
              const b = d.funnel[k];
              return (
                <div className="si-hbar-row" key={k}>
                  <span className="si-hbar-lbl">{k}</span>
                  <div className="si-hbar-track"><div className={'si-hbar' + (k === 'Processed' ? ' good' : k === 'Open' ? '' : ' muted')} style={{ width: Math.max(3, b.count / funnelMax * 100) + '%' }} /></div>
                  <span className="si-hbar-val">{b.count} · {fmtMoney(b.amount, cur)}</span>
                </div>
              );
            })}
          </div>
          <div className="si-panel" data-testid="si-clients-revenue">
            <h4>Top clients by revenue (orders)</h4>
            {(d.clients?.top_by_revenue || []).length === 0 ? <div className="empty-note" style={{ border: 'none' }}>No order data.</div> : (
              <table className="si-table"><tbody>
                {d.clients.top_by_revenue.map(c => (
                  <tr key={c.name}>
                    <td className="si-item-name" title={c.name}>{c.name}</td>
                    <td className="num">{c.count}</td>
                    <td className="num strong">{fmtMoney(c.amount, cur)}</td>
                    <td style={{ width: 30 }}>
                      <button className="btn btn-sm" data-testid={'si-client-export-' + c.name.replace(/\s+/g, '-')}
                        disabled={exporting === c.name} title={'Export ' + c.name + "'s full breakdown to Excel"}
                        onClick={() => exportClient(c.name)}>{exporting === c.name ? '…' : '⬇'}</button>
                    </td>
                  </tr>
                ))}
              </tbody></table>
            )}
          </div>
        </div>

        {(d.revenue_monthly || []).length > 1 && (
          <SiBarChart testid="si-revenue-trend" cur={cur} primary="amount" unit="order" tone="green"
            title="Sales order revenue per month"
            buckets={d.revenue_monthly.map(m => ({ key: m.month, count: m.count, amount: m.amount }))} />
        )}

        <div className="si-items" data-testid="si-leaderboard" style={{ marginBottom: 18 }}>
          <h4>Team leaderboard</h4>
          <table className="si-table si-items-table">
            <thead><tr><th>Person</th><th>Group</th><th className="num">Quotes</th><th className="num">Quoted $</th><th className="num" title="Quotes from this person that produced a sales order — quote-level, unlike the Overview True wins card which is opportunity-level">Quotes converted</th><th className="num">Win %</th><th className="num">Won $</th><th className="num">Avg close</th><th className="num">Avg quoted GM%</th></tr></thead>
            <tbody>
              {(d.leaderboard || []).map(r => (
                <tr key={r.name} data-testid={'si-lb-' + r.name.replace(/\s+/g, '-')}>
                  <td className="si-item-name" title={r.name}>{r.name}</td>
                  <td>{r.group !== 'Unassigned' ? <span className="si-group-pill">{r.group}</span> : <span className="si-stock-na">—</span>}</td>
                  <td className="num">{r.quotes.toLocaleString()}</td>
                  <td className="num">{fmtMoney(r.amount, cur)}</td>
                  <td className="num">{r.true_wins.toLocaleString()}</td>
                  <td className="num strong">{r.win_pct != null ? r.win_pct + '%' : '—'}</td>
                  <td className="num">{fmtMoney(r.win_amount, cur)}</td>
                  <td className="num">{r.avg_days_to_close != null ? Math.round(r.avg_days_to_close) + 'd' : '—'}</td>
                  <td className="num">{r.avg_gm_pct != null ? r.avg_gm_pct + '%' : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="si-grid">
          <DistPanel testid="si-size-dist" title="Quote size distribution" dist={d.quote_size_dist} cur={cur} />
          <DistPanel testid="si-close-dist" title="Time to close (won quotes)" dist={d.close_time_dist} cur={cur} />
          <div className="si-panel" data-testid="si-movers">
            <h4>Item demand movers</h4>
            {(d.item_movers?.up || []).length === 0 && (d.item_movers?.down || []).length === 0
              ? <div className="empty-note" style={{ border: 'none' }}>Not enough movement in this window.</div>
              : (
                <table className="si-table"><tbody>
                  {(d.item_movers.up || []).map(m => (
                    <tr key={m.item_id}><td className="si-item-name" title={m.itemid}>{m.itemid}</td><td className="num">{m.recent_qty.toLocaleString()}</td><td className="num"><span className="si-move up">▲ {m.is_new ? 'new' : (m.change_pct >= 999 ? '999%+' : m.change_pct + '%')}</span></td></tr>
                  ))}
                  {(d.item_movers.down || []).map(m => (
                    <tr key={m.item_id}><td className="si-item-name" title={m.itemid}>{m.itemid}</td><td className="num">{m.recent_qty.toLocaleString()}</td><td className="num"><span className="si-move down">▼ {Math.abs(m.change_pct)}%</span></td></tr>
                  ))}
                </tbody></table>
              )}
            <div className="field-hint" style={{ marginTop: 6 }}>Recent half vs previous half of the window, by quoted + ordered qty.</div>
          </div>
        </div>
      </section>
    </>
  );
}

function DistPanel({ title, dist, cur, testid }) {
  const entries = Object.entries(dist || {});
  const max = Math.max(...entries.map(([, v]) => v.count), 1);
  return (
    <div className="si-panel" data-testid={testid}>
      <h4>{title}</h4>
      {entries.map(([lbl, v]) => (
        <div className="si-hbar-row" key={lbl}>
          <span className="si-hbar-lbl wide">{lbl}</span>
          <div className="si-hbar-track"><div className="si-hbar" style={{ width: Math.max(3, v.count / max * 100) + '%' }} /></div>
          <span className="si-hbar-val">{v.count} · {fmtMoney(v.amount, cur)}</span>
        </div>
      ))}
    </div>
  );
}

const pct = v => v == null ? '—' : (v > 0 ? '+' : '') + v + '%';
const pctStyle = v => ({ color: v == null ? '#94a3b8' : v >= 0 ? '#15803d' : '#dc2626', fontWeight: 700 });

function OrdersFinance({ segment, subsidiary, toast }) {
  const [d, setD] = useState(null);
  const [packMonth, setPackMonth] = useState(() => {
    const t = new Date(); t.setDate(0); // last day of previous month
    return t.getFullYear() + '-' + String(t.getMonth() + 1).padStart(2, '0');
  });
  const [packBusy, setPackBusy] = useState(false);
  useEffect(() => { api.ordersFinance(segment, subsidiary).then(setD).catch(() => {}); }, [segment, subsidiary]);
  async function downloadPack() {
    setPackBusy(true);
    try {
      const blob = await api.financePackExport(packMonth, segment, subsidiary);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'finance-pack-' + packMonth + (subsidiary ? '-' + subsidiary.replace(/\s+/g, '-').toLowerCase() : '') + '.xlsx';
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast && toast('Finance Pack downloaded — finance view + win probability + alerts in one workbook');
    } catch (e) { toast && toast('Finance Pack failed: ' + (e.response?.data?.detail || e.message), true); }
    setPackBusy(false);
  }
  if (!d) return <section className="dash-card si-card"><div className="empty-note">Loading finance view…</div></section>;
  const cur = d.currency;
  const k = d.kpis || {};
  const months = (d.months || []).slice().reverse();
  return (
    <section className="dash-card si-card" data-testid="si-orders-finance">
      <div className="dash-card-head si-head">
        <h3>Sales Orders — Finance View <span className="dash-flag-tag si-tag">{d.segment}{d.subsidiary && d.subsidiary !== 'All' ? ' · ' + d.subsidiary : ''}</span></h3>
        <span className="si-forecast-basis">Booked sales orders · month-to-month with prior-year comparisons against fully logged months</span>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }} data-testid="finance-pack-controls">
          <input type="month" className="input" data-testid="finance-pack-month" value={packMonth}
            onChange={e => setPackMonth(e.target.value)} style={{ maxWidth: 160, padding: '4px 8px' }} />
          <button className="btn btn-sm btn-primary" data-testid="finance-pack-download" disabled={packBusy || !packMonth}
            title="Month-end Excel pack: finance view + win probability + probability alerts"
            onClick={downloadPack}>{packBusy ? 'Building…' : '⬇ Finance Pack'}</button>
        </div>
      </div>
      <div className="si-kpis">
        <div className="si-kpi"><span className="si-kpi-num">{fmtMoney(k.mtd_revenue, cur)}</span><span className="si-kpi-lbl">This month ({k.mtd_count} orders) · <b style={pctStyle(k.mom_pct)}>{pct(k.mom_pct)}</b> vs last month to date{k.ly_mtd_revenue != null && <> · <b style={pctStyle(k.yoy_pct)}>{pct(k.yoy_pct)}</b> vs LY to date</>}</span></div>
        <div className="si-kpi"><span className="si-kpi-num">{fmtMoney(k.last_month_revenue, cur)}</span><span className="si-kpi-lbl">Last month</span></div>
        <div className="si-kpi"><span className="si-kpi-num">{fmtMoney(k.quarter_revenue, cur)}</span><span className="si-kpi-lbl">This quarter · {k.quarter_ly_revenue != null ? <><b style={pctStyle(k.quarter_pct)}>{pct(k.quarter_pct)}</b> vs LY ({fmtMoney(k.quarter_ly_revenue, cur)})</> : <span data-testid="si-finance-q-noly">LY not fully logged yet</span>}</span></div>
        <div className="si-kpi"><span className="si-kpi-num">{fmtMoney(k.ytd_revenue, cur)}</span><span className="si-kpi-lbl">Year to date · {k.ytd_ly_revenue != null ? <><b style={pctStyle(k.ytd_pct)}>{pct(k.ytd_pct)}</b> vs LY ({fmtMoney(k.ytd_ly_revenue, cur)})</> : <span data-testid="si-finance-ytd-noly">LY not fully logged yet</span>}</span></div>
      </div>
      {d.ly_note && <p className="si-forecast-basis" data-testid="si-finance-ly-note" style={{ margin: "6px 0 0" }}>{d.ly_note} Monthly totals are archived on every sync, so history keeps growing beyond the 12-month NetSuite window.</p>}
      <div className="si-grid">
        <div className="si-panel" style={{ gridColumn: '1 / -1' }} data-testid="si-finance-monthly">
          <h4>Month by month ({months.length} months logged{d.history_from ? " · since " + d.history_from : ""})</h4>
          <table className="si-table">
            <thead><tr><th style={{ textAlign: 'left' }}>Month</th><th className="num">Orders</th><th className="num">Revenue</th><th className="num">Avg order</th><th className="num">GM % est.</th><th className="num">MoM</th><th className="num">vs LY</th></tr></thead>
            <tbody>
              {months.map(m => (
                <tr key={m.month}>
                  <td style={{ fontWeight: 700 }}>{m.month}{m.partial && <span className="si-forecast-basis" style={{ marginLeft: 6, fontWeight: 500 }}>MTD</span>}</td>
                  <td className="num">{m.count.toLocaleString()}</td>
                  <td className="num strong">{fmtMoney(m.revenue, cur)}</td>
                  <td className="num">{m.avg_order != null ? fmtMoney(m.avg_order, cur) : '—'}</td>
                  <td className="num">{m.gm_pct != null ? m.gm_pct + '%' : '—'}</td>
                  <td className="num" style={pctStyle(m.mom_pct)}>{pct(m.mom_pct)}</td>
                  <td className="num" style={pctStyle(m.yoy_pct)}>{pct(m.yoy_pct)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {(d.top_parents || []).length > 0 && (
          <div className="si-panel" data-testid="si-finance-parents">
            <h4>Top parent companies — SO revenue (12 mo)</h4>
            <table className="si-table"><tbody>
              {d.top_parents.map(p => (
                <tr key={p.name}><td className="si-item-name" title={p.name}>{p.name}</td><td className="num">{p.count}</td><td className="num strong">{fmtMoney(p.amount, cur)}</td></tr>
              ))}
            </tbody></table>
          </div>
        )}
        <div className="si-panel" data-testid="si-finance-clients">
          <h4>Top clients — SO revenue (12 mo)</h4>
          <table className="si-table"><tbody>
            {(d.top_clients || []).map(c => (
              <tr key={c.name}><td className="si-item-name" title={c.name}>{c.name}</td><td className="num">{c.count}</td><td className="num strong">{fmtMoney(c.amount, cur)}</td></tr>
            ))}
          </tbody></table>
        </div>
      </div>
    </section>
  );
}

function ProbabilityAlerts({ subsidiary }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.salesIntelAlerts(subsidiary).then(setD).catch(() => {}); }, [subsidiary]);
  if (!d) return null;
  const alerts = d.alerts || [];
  return (
    <section className="dash-card si-card" data-testid="si-prob-alerts">
      <div className="dash-card-head si-head">
        <h3>Probability Alerts <span className="dash-flag-tag si-tag" style={alerts.length ? { background: '#dc2626', color: '#fff' } : {}}>{alerts.length ? alerts.length + ' alert' + (alerts.length === 1 ? '' : 's') : 'All clear'}</span></h3>
        <span className="si-forecast-basis">
          Open opportunities worth A${Math.round(d.threshold).toLocaleString()}+ whose win probability dropped vs ~a week ago ({d.week_basis}) — from nightly model snapshots.
        </span>
      </div>
      {alerts.length === 0 ? (
        <div className="empty-note" data-testid="prob-alerts-empty">
          {d.no_history >= d.tracked && d.tracked > 0
            ? `No week-over-week comparison yet — nightly snapshots started ${d.history_start || 'today'}. Alerts appear once a week of history builds up (tracking ${d.tracked} high-value opportunities).`
            : `No probability declines this week across ${d.tracked} tracked high-value opportunities.`}
        </div>
      ) : (
        <div className="si-panel" style={{ overflowX: 'auto' }} data-testid="prob-alerts-table">
          <table className="si-table" style={{ minWidth: 860 }}>
            <thead><tr>{['Opp #', 'Client', 'Rep', 'Subsidiary', 'Value', 'Was', 'Now', 'Drop', 'Expected impact'].map(h => <th key={h} className={['Value', 'Was', 'Now', 'Drop', 'Expected impact'].includes(h) ? 'num' : ''} style={{ textAlign: 'left' }}>{h}</th>)}</tr></thead>
            <tbody>
              {alerts.map(a => (
                <tr key={a.tranid} data-testid={'prob-alert-' + a.tranid}>
                  <td style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{a.tranid}</td>
                  <td className="si-item-name" title={a.client}>{a.client}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{a.salesrep || '—'}</td>
                  <td>{a.subsidiary}</td>
                  <td className="num strong">{fmtMoney(a.amount, 'AUD')}</td>
                  <td className="num">{Math.round(a.prev_p * 100)}%</td>
                  <td className="num" style={{ color: '#dc2626', fontWeight: 800 }}>{Math.round((a.win_p || 0) * 100)}%</td>
                  <td className="num" style={{ color: '#dc2626', fontWeight: 800 }}>▼ {a.drop_pts} pts</td>
                  <td className="num">{fmtMoney(a.expected_impact, 'AUD')}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="field-hint" style={{ marginTop: 6 }}>Expected impact = opportunity value × probability drop (how much expected pipeline value slipped away this week).</div>
        </div>
      )}
    </section>
  );
}

function WinProbability({ subsidiary }) {
  const [d, setD] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [busy, setBusy] = useState(false);
  const sub = subsidiary || 'All';
  const load = useCallback(() => { api.winProbability(subsidiary || '').then(setD).catch(() => {}); }, [subsidiary]);
  useEffect(() => { load(); }, [load]);
  async function refresh() {
    setBusy(true);
    try { await api.winProbabilitySync(); setTimeout(() => { load(); setBusy(false); }, 45000); }
    catch (e) { setBusy(false); }
  }
  async function exportXlsx() {
    const blob = await api.winProbabilityExport(subsidiary || '');
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'win-probability-' + sub.toLowerCase() + '.xlsx';
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  }
  if (!d) return <section className="dash-card si-card"><div className="empty-note">Loading win probability…</div></section>;
  const k = d.kpis || {};
  const meta = d.meta || {};
  const shown = showAll ? d.opps : (d.opps || []).slice(0, 15);
  const pcol = v => ({ color: v >= 0.75 ? '#15803d' : v >= 0.4 ? '#b45309' : '#dc2626', fontWeight: 800 });
  return (
    <section className="dash-card si-card" data-testid="si-win-probability">
      <div className="dash-card-head si-head">
        <h3>Win Probability — Open Opportunities <span className="dash-flag-tag si-tag">{sub === 'All' ? 'All subsidiaries' : sub}</span></h3>
        <span className="si-forecast-basis">
          Modelled from {(meta.trained_on || 0).toLocaleString()} historical closed opps (days open · deal value · retail/commercial · repeat client · rep win rate) × recency multiplier. Rebuilt nightly.
        </span>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <button className="btn btn-sm" data-testid="winprob-export" onClick={exportXlsx}>⬇ Export</button>
          <button className="btn btn-sm" data-testid="winprob-refresh" disabled={busy} onClick={refresh}>{busy ? 'Rescoring…' : '↻ Resync & rescore'}</button>
        </div>
      </div>
      {(d.opps || []).length === 0 ? (
        <div className="empty-note" data-testid="winprob-empty">No scored opportunities yet — hit "Resync & rescore" to pull opportunities from NetSuite (takes ~a minute).</div>
      ) : (
        <>
          <div className="si-kpis" data-testid="winprob-kpis">
            <div className="si-kpi"><span className="si-kpi-num">{(k.open_count || 0).toLocaleString()}</span><span className="si-kpi-lbl">Open opportunities</span></div>
            <div className="si-kpi"><span className="si-kpi-num">{fmtMoney(k.open_value, 'AUD')}</span><span className="si-kpi-lbl">Total open pipeline</span></div>
            <div className="si-kpi"><span className="si-kpi-num">{fmtMoney(k.expected_value, 'AUD')}</span><span className="si-kpi-lbl">Probability-weighted expected value</span></div>
            <div className="si-kpi"><span className="si-kpi-num">{k.blended_pct != null ? k.blended_pct + '%' : '—'}</span><span className="si-kpi-lbl">Blended win likelihood</span></div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '4px 0 12px' }} data-testid="winprob-tiers">
            {(d.tiers || []).map(t => (
              <span key={t.tier} className="pc-ds-pill" style={{ fontSize: 12 }}>
                <b>{t.tier}</b>: {t.count} opps · {fmtMoney(t.amount, 'AUD')} → {fmtMoney(t.expected, 'AUD')} expected
              </span>
            ))}
          </div>
          <div className="si-panel" style={{ overflowX: 'auto' }} data-testid="winprob-table">
            <h4>Scored opportunities (highest expected value first)</h4>
            <table className="si-table" style={{ minWidth: 900 }}>
              <thead><tr>{['Opp #', 'Client', 'Rep', 'Subsidiary', 'Sector', 'Repeat', 'Value', 'Days open', 'Recency', 'Model %', 'Win %'].map(h => <th key={h} className={['Value', 'Days open', 'Model %', 'Win %'].includes(h) ? 'num' : ''} style={{ textAlign: 'left' }}>{h}</th>)}</tr></thead>
              <tbody>
                {shown.map(o => (
                  <tr key={o.tranid}>
                    <td style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{o.tranid}</td>
                    <td className="si-item-name" title={o.client}>{o.client}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{o.salesrep || '—'}</td>
                    <td>{o.subsidiary}</td><td>{o.sector}</td><td>{o.is_repeat}</td>
                    <td className="num strong">{fmtMoney(o.amount, 'AUD')}</td>
                    <td className="num">{o.days_open_now}d</td>
                    <td style={{ fontSize: 11.5 }}>{o.recency_tier}</td>
                    <td className="num">{Math.round((o.model_p || 0) * 100)}%</td>
                    <td className="num" style={pcol(o.win_p)}>{Math.round((o.win_p || 0) * 100)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(d.opps || []).length > 15 && (
              <button className="si-expand" data-testid="winprob-expand" onClick={() => setShowAll(v => !v)}>
                {showAll ? '▴ Show top 15' : `▾ Show all ${d.opps.length}`}
              </button>
            )}
          </div>
        </>
      )}
    </section>
  );
}
